import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FiAlertTriangle, FiClock, FiXCircle, FiChevronRight } from 'react-icons/fi';
import { timeEntryService } from '../../services/api';
import { formatHoursMinutes, formatMinutes } from '../../utils/formatHours';

/* Palette shared with the calendar (REQ #9) so colours mean the same thing. */
const C = { green: 'var(--act-approve)', red: 'var(--act-danger)', action: 'var(--act-primary)', yellow: 'var(--status-warning)', ink: 'var(--ve-ink-800)', muted: 'var(--ve-ink-600)', line: 'var(--border-subtle)', bg: 'var(--surface-subtle)' };

const Page = styled.div`max-width: 820px;`;
const Cards = styled.div`display:grid;grid-template-columns:repeat(3,1fr);gap:.8rem;margin-bottom:1.4rem;@media(max-width:576px){grid-template-columns:1fr;}`;
const StatCard = styled.button`display:flex;align-items:center;gap:.8rem;padding:1rem;border-radius:.55rem;border:1px solid ${C.line};border-left:4px solid ${({ $c }) => $c};background:#fff;cursor:pointer;text-align:left;&:hover{filter:brightness(.98);}`;
const StatIcon = styled.div`color:${({ $c }) => $c};font-size:1.5rem;display:inline-flex;`;
const StatNum = styled.div`font-size:1.6rem;font-weight:700;color:${C.ink};line-height:1;`;
const StatLabel = styled.div`font-size:.82rem;color:${C.muted};margin-top:.2rem;`;

const Section = styled.section`background:#fff;border:1px solid ${C.line};border-radius:.55rem;margin-bottom:1.1rem;overflow:hidden;`;
const SecHead = styled.div`display:flex;align-items:center;gap:.5rem;padding:.8rem 1rem;border-bottom:1px solid ${C.line};font-weight:700;color:${C.ink};`;
const SecDot = styled.span`display:inline-block;width:10px;height:10px;border-radius:3px;background:${({ $c }) => $c};`;
const Item = styled.button`width:100%;display:flex;align-items:center;justify-content:space-between;gap:.6rem;padding:.7rem 1rem;border:none;border-bottom:1px solid ${C.line};background:#fff;cursor:pointer;text-align:left;&:last-child{border-bottom:none;}&:hover{background:#f7f9fb;}`;
const ItemDate = styled.div`font-weight:600;color:${C.ink};font-size:.9rem;text-transform:capitalize;`;
const ItemMeta = styled.div`font-size:.82rem;color:${C.muted};`;
const HoursCmp = styled.div`font-size:.85rem;color:${C.ink};display:flex;align-items:center;gap:.35rem;`;
const Old = styled.span`text-decoration:line-through;color:${C.muted};`;
const New = styled.span`font-weight:700;color:${C.red};`;
const Empty = styled.div`padding:1.1rem;text-align:center;color:${C.muted};font-size:.88rem;`;
const AllGood = styled.div`padding:1.4rem;text-align:center;color:${C.green};font-weight:600;`;
// Running month-to-date total (REQ #3).
const MonthBanner = styled.div`display:flex;align-items:baseline;justify-content:space-between;gap:.6rem;flex-wrap:wrap;background:#fff;border:1px solid ${C.line};border-left:4px solid ${C.action};border-radius:.55rem;padding:.9rem 1.1rem;margin-bottom:1.2rem;`;
const MonthBannerLabel = styled.div`font-size:.85rem;color:${C.muted};`;
const MonthBannerNum = styled.div`font-size:1.5rem;font-weight:700;color:${C.ink};`;
const Loading = styled.div`padding:2rem;text-align:center;color:${C.muted};`;
const ErrBox = styled.div`background:var(--status-danger-plate);color:${C.red};border:1px solid #f5c6cb;padding:.7rem 1rem;border-radius:.4rem;margin-bottom:1rem;`;

const Dashboard = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await timeEntryService.getEmployeeDashboard()); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Every date links to the calendar (where a day opens its entry / contest modal).
  const openCalendar = () => navigate('/employee/calendar');

  const fmt = (d) => {
    try { return new Date(d + 'T00:00:00').toLocaleDateString(i18n.language, { weekday: 'short', day: 'numeric', month: 'short' }); }
    catch (e) { return d; }
  };

  if (loading) return <Page><Loading>{t('common.loading', { defaultValue: 'Loading…' })}</Loading></Page>;

  const counts = (data && data.counts) || { missing: 0, contested: 0, awaiting: 0 };
  const missing = (data && data.missing) || [];
  const contested = (data && data.contested) || [];
  const awaiting = (data && data.awaiting) || [];
  // Only reassure the employee when the data actually loaded — never on an error
  // (where counts default to all-zero and would falsely show "all caught up").
  const allGood = !error && !!data && counts.missing === 0 && counts.contested === 0 && counts.awaiting === 0;

  return (
    <Page>
      {error && <ErrBox>{error}</ErrBox>}

      <MonthBanner>
        <MonthBannerLabel>{t('employee.dash.monthTotal', { defaultValue: 'Total hours this month' })}</MonthBannerLabel>
        {/* H:MM from the canonical minute total; the banner label already says "hours". */}
        <MonthBannerNum>{formatMinutes(data && data.monthMinutes, '0:00')}</MonthBannerNum>
      </MonthBanner>

      <Cards>
        <StatCard $c={C.yellow} onClick={openCalendar}>
          <StatIcon $c={C.yellow}><FiAlertTriangle /></StatIcon>
          <div><StatNum>{counts.missing}</StatNum><StatLabel>{t('employee.dash.missing', { defaultValue: 'Missing registrations' })}</StatLabel></div>
        </StatCard>
        <StatCard $c={C.red} onClick={openCalendar}>
          <StatIcon $c={C.red}><FiXCircle /></StatIcon>
          <div><StatNum>{counts.contested}</StatNum><StatLabel>{t('employee.dash.contested', { defaultValue: 'Contested days' })}</StatLabel></div>
        </StatCard>
        <StatCard $c={C.action} onClick={openCalendar}>
          <StatIcon $c={C.action}><FiClock /></StatIcon>
          <div><StatNum>{counts.awaiting}</StatNum><StatLabel>{t('employee.dash.awaiting', { defaultValue: 'Awaiting approval' })}</StatLabel></div>
        </StatCard>
      </Cards>

      {allGood && <AllGood>{t('employee.dash.allGood', { defaultValue: 'You are all caught up — nothing needs your attention.' })}</AllGood>}

      {counts.contested > 0 && (
        <Section>
          <SecHead><SecDot $c={C.red} />{t('employee.dash.contestedTitle', { defaultValue: 'Contested days — action needed' })}</SecHead>
          {contested.map((c) => (
            <Item key={c.date} onClick={openCalendar}>
              <div>
                <ItemDate>{fmt(c.date)}</ItemDate>
                <ItemMeta>{t('employee.dash.tapToResolve', { defaultValue: 'Tap to accept or disagree' })}</ItemMeta>
              </div>
              <HoursCmp>
                <Old>{formatHoursMinutes(c.loggedHours)}</Old>
                <FiChevronRight />
                <New>{formatHoursMinutes(c.proposedHours)}</New>
              </HoursCmp>
            </Item>
          ))}
        </Section>
      )}

      {counts.missing > 0 && (
        <Section>
          <SecHead><SecDot $c={C.yellow} />{t('employee.dash.missingTitle', { defaultValue: 'Days still to register' })}</SecHead>
          {missing.slice(0, 40).map((d) => (
            <Item key={d} onClick={openCalendar}>
              <ItemDate>{fmt(d)}</ItemDate>
              <ItemMeta>{t('employee.dash.tapToRegister', { defaultValue: 'Tap to register' })} <FiChevronRight style={{ verticalAlign: 'middle' }} /></ItemMeta>
            </Item>
          ))}
          {missing.length > 40 && <Empty>{t('employee.dash.more', { defaultValue: '…and {{n}} more', n: missing.length - 40 })}</Empty>}
        </Section>
      )}

      {counts.awaiting > 0 && (
        <Section>
          <SecHead><SecDot $c={C.action} />{t('employee.dash.awaitingTitle', { defaultValue: 'Awaiting customer approval' })}</SecHead>
          {awaiting.map((a) => (
            <Item key={a.date} onClick={openCalendar}>
              <ItemDate>{fmt(a.date)}</ItemDate>
              {/* Label first, value in H:MM — a bare "7:30" beside a date could read as a clock time. */}
              <ItemMeta>{t('common.hours')}: {formatHoursMinutes(a.hours)}</ItemMeta>
            </Item>
          ))}
        </Section>
      )}
    </Page>
  );
};

export default Dashboard;
